var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/**
 * Raw form inputs without labels
 */
import { NumberInput } from '@linked/controls';
import { makeStyles, Popover, styled, IconButton } from '@material-ui/core';
import { withStyles } from '@material-ui/styles';
import { useLinked } from '@type-r/react';
import * as React from 'react';
import { useContext, forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import { colors, div } from '../common';
import { asCurrency } from '../formatters';
import CloseIcon from '@material-ui/icons/Close';
export var ReadOnly = React.createContext(false);
var DateInputElement = forwardRef(function (props, ref) { return (React.createElement("input", __assign({}, props, { ref: ref }))); });
/**
 * Text, numeric, and date inputs are based on `BaseInput` component and share the same props and behavior.
 *
 * - `BaseInput` is not styled and not intended to be used directly.
 * - `Input` - styled version of BaseInput, use as an inline input without the label (in grids).
 * - `FormInput` - Input with horizontally alighed label displaying inline error text. Main building block of forms.
 * - `CompactInput` - Input with embedded label to be used in forms with vertical layout.
 */
export var BaseInput = function (_a) {
    var $value = _a.$value, value = _a.value, classes = _a.classes, placeholder = _a.placeholder, readOnly = _a.readOnly, number = _a.number, currency = _a.currency, className = _a.className, date = _a.date, positive = _a.positive, integer = _a.integer, props = __rest(_a, ["$value", "value", "classes", "placeholder", "readOnly", "number", "currency", "className", "date", "positive", "integer"]);
    var ro = useContext(ReadOnly) || readOnly || !$value;
    var cs = className ? classes.input + ' ' + className : classes.input;
    if (!ro && $value.error && !props.disabled && !($value.error === 'Required' && props.required === false))
        cs += ' ' + classes.error;
    if (date) {
        return ro ?
            React.createElement(DatePicker, __assign({ dateFormat: "MM/dd/yyyy", placeholderText: placeholder || 'MM/DD/YYYY', onChange: function () { return undefined; }, readOnly: true, customInput: React.createElement(DateInputElement, { "data-qa-id": props['data-qa-id'] }), className: cs, selected: ($value ? $value.value : value) }, props))
            :
                React.createElement(DatePicker, __assign({ dateFormat: "MM/dd/yyyy", placeholderText: placeholder || 'MM/DD/YYYY', className: cs + ' date-picker', popperContainer: StopClicks, popperPlacement: 'auto ', customInput: React.createElement(DateInputElement, { "data-qa-id": props['data-qa-id'] }), selected: $value.value, onChange: function (date) { return $value.set(date); } }, props));
    }
    var isNumber = number || positive || integer || currency;
    if (isNumber)
        cs += ' ' + classes.number;
    return !ro && isNumber ?
        React.createElement(NumberInput, __assign({}, props, { positive: positive, placeholder: placeholder, integer: integer, className: cs, "$value": $value }))
        :
            React.createElement("input", __assign({ readOnly: ro, className: cs, placeholder: placeholder, value: valueToText($value, value, currency && ro ? asCurrency : dummy), onChange: function (e) { return $value.set(e.target.value); } }, props));
};
var StopClicks = function (_a) {
    var children = _a.children;
    return React.createElement("div", { onClick: function (e) {
            e.preventDefault();
        } }, children);
};
export var inputStyles = {
    '&.date-picker': {
        background: 'url(assets/images/Icon_Calendar.png) no-repeat scroll',
        paddingRight: 5,
        backgroundOrigin: 'content-box',
        backgroundPosition: 'center right',
    }
};
export var Input = withStyles({
    input: __assign({}, inputStyles, { width: '100%', borderWidth: 1, borderStyle: 'solid', boxSizing: 'border-box', borderRadius: 2, backgroundColor: '#FFF', border: '1px solid #E3E9ED', color: '#000', fontFamily: 'Montserrat', fontSize: 14, fontWeight: 500, padding: '4px 3px 3px 3px', '&:focus': {
            backgroundColor: '#F6FCFF',
            border: '1px solid #1084D3',
            outline: 'none',
        }, '&:disabled': {
            backgroundColor: 'rgba(227,233,237,0.25)',
            border: '1px solid #E3E9ED',
        }, '&:read-only': {
            border: 'none'
        }, '&:hover:read-write': {
            border: '1px solid #1084D3',
        } }),
    number: {
        textAlign: 'end'
    },
    error: {
        border: '1px solid #FF274B !important',
    }
})(BaseInput);
function dummy(x) {
    return x;
}
function valueToText($value, value, formatter) {
    if (formatter === void 0) { formatter = dummy; }
    var x = $value ? $value.value : value;
    return x == null ? '' : formatter(x);
}
function maybeError(className, $value) {
    return $value && $value.error ? className + ' error' : className;
}
export var PopoverValue = function (_a) {
    var label = _a.label, children = _a.children, _b = _a.withCloseIcon, withCloseIcon = _b === void 0 ? true : _b, _c = _a.anchorAlign, anchorAlign = _c === void 0 ? null : _c, _d = _a.transformAlign, transformAlign = _d === void 0 ? null : _d;
    var $open = useLinked(null), _e = usePopoverValueStyles(), labelClass = _e.label, iconButtonClass = _e.iconButton, classes = __rest(_e, ["label", "iconButton"]);
    return (React.createElement(React.Fragment, null,
        React.createElement("span", { className: labelClass, onClick: function (event) { return $open.set(event.currentTarget); } }, label),
        React.createElement(Popover, { classes: classes, open: Boolean($open.value), anchorEl: $open.value, onClose: $open.null, anchorOrigin: {
                vertical: 'bottom',
                horizontal: anchorAlign || 'center',
            }, transformOrigin: {
                vertical: 'top',
                horizontal: transformAlign || 'center',
            } },
            children,
            withCloseIcon ?
                React.createElement(IconButton, { "aria-label": "Close", size: "small", className: iconButtonClass, onClick: $open.null },
                    React.createElement(CloseIcon, null)) : undefined)));
};
var usePopoverValueStyles = makeStyles({
    label: {
        "&:hover": {
            textDecoration: 'underline',
            cursor: 'pointer'
        },
        fontStyle: 'italic'
    },
    paper: {
        padding: 8
    },
    iconButton: {
        position: 'absolute',
        top: 7,
        right: 7
    }
});
export var FormValue = div({
    fontFamily: 'Montserrat',
    flex: 3,
    color: 'black',
    alignSelf: 'center',
    fontSize: 14,
    fontWeight: 500,
});
export var PrevFormValue = div({
    fontFamily: 'Montserrat',
    flex: 3,
    alignSelf: 'center',
    fontSize: 14,
    fontWeight: 500,
    textDecoration: 'line-through',
    color: colors.red
});
function dummyFormatter(x) {
    return x;
}
export var Changes = styled(function (_a) {
    var children = _a.children, prev = _a.prev, className = _a.className, _b = _a.formatter, formatter = _b === void 0 ? dummyFormatter : _b;
    var changed = children !== prev;
    return changed ?
        React.createElement("div", { className: className },
            prev ? React.createElement("div", { className: "prev-value" }, formatter(prev)) : null,
            React.createElement("div", { className: "curr-value" }, formatter(children)))
        :
            formatter(children);
})({
    '& .curr-value': {
        color: colors.red,
        backgroundColor: '#FCF8F8'
    },
    '& .prev-value': {
        textDecoration: 'line-through',
        color: colors.grey,
        width: "100%"
    }
});
var defaultRenderer = function (x) { return React.createElement(FormValue, { key: x }, x); };
export var PopoverList = function (_a) {
    var items = _a.items, _b = _a.children, children = _b === void 0 ? defaultRenderer : _b;
    return items.length > 1 ?
        React.createElement(PopoverValue, { label: "Click to show (" + items.length + " items)" }, items.map(function (x) { return children(x); }))
        :
            items.length ? children(items[0]) :
                React.createElement("div", null);
};
//# sourceMappingURL=inputs.js.map